import React, { useMemo, FC, ReactNode } from 'react';
import { Fade } from 'react-awesome-reveal';
import Link from 'next/link';
import Image from 'next/image';
import { Button, P, EpisodeProps, PODCAST_URL, ShareRouter } from '@acueducto/shared';
import BroadcastRouter from '../BroadcastRouter';
import EpisodeNumber from '../EpisodeNumber';

import {
  ToBeReleased,
  NewPod,
  PictureContainer,
  HoverableContainer,
  Guest,
  DateCat,
  ButtonSpace,
  TitleHoverable,
} from './EpisodePreview.styles';

const formatDate = (date: string | undefined): string => {
  const fullDate = new Date(`${date}T00:00:00`);
  return fullDate.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getShortDate = (date: string | undefined): string => {
  const fullDate = new Date(`${date}T00:00:00`);
  return fullDate.toLocaleDateString('es-MX');
};

interface EpisodePreviewProps extends EpisodeProps {
  longFormat?: boolean;
  simplest?: boolean;
  text?: string;
  hideImageMobile?: boolean;
  imagePriority?: boolean;
}

const LinkComplex: FC<{ slug: string; children: ReactNode }> = ({ slug, children }) => (
  <Link href={`/${slug}`} className="clean">
    {children}
  </Link>
);

const PreEpisodePreview: React.FC<EpisodePreviewProps> = ({
  title,
  guest,
  business,
  description,
  category,
  slug,
  date,
  spotify,
  apple,
  youtube,
  episodeNumber,
  longFormat = false,
  simplest = false,
  text = '',
  hideImageMobile = false,
  imagePriority = false,
  podcastCoverImage = null,
}) => {
  const formattedDate = useMemo(() => date ? formatDate(date) : '', [date]);
  const shortDate = useMemo(() => date ? getShortDate(date) : '', [date]);


  return (
    <NewPod key={'npd' + episodeNumber} $simplest={simplest}>
      <PictureContainer
        $hoverable={!longFormat}
        $hideImageMobile={hideImageMobile}
      >
        <Fade triggerOnce>
          {podcastCoverImage && (
            longFormat ? (
              <Image
                src={podcastCoverImage}
                width={180}
                height={180}
                alt={`${business} - ${guest}`}
                loading="lazy"
              />
            ) : (
              <LinkComplex slug={slug}>
                <Image
                  src={podcastCoverImage}
                  height={simplest ? 185 : 180}
                  width={simplest ? 185 : 180}
                  alt={`${business} - ${guest}`}
                  priority={imagePriority}
                />
              </LinkComplex>
            )
          )}
        </Fade>
      </PictureContainer>
      <div>
        <Fade triggerOnce>
          <HoverableContainer>
            {!longFormat && !simplest && (
              <LinkComplex slug={slug}>
                <TitleHoverable>{title}</TitleHoverable>
              </LinkComplex>
            )}
          </HoverableContainer>
          <Guest>
            {!longFormat ? (
              <LinkComplex slug={slug}>
                <EpisodeNumber episodeNumber={episodeNumber} />
                <p className="guest">
                  {guest} <span>{business}</span>
                </p>
              </LinkComplex>
            ) : (
              <h2 className="guest">
                {guest} <span>{business}</span> <strong>{title}</strong>
              </h2>
            )}
          </Guest>
          <DateCat>
            {longFormat && spotify && (
              <time dateTime={date}>{formattedDate}</time>
            )}
            <span>{category}</span>
          </DateCat>
          <div>{!simplest && <P>{description}</P>}</div>
          <div>
            {!simplest &&
              (spotify ? (
                <BroadcastRouter
                  trackClicks
                  episodeNumber={episodeNumber}
                  spotify={spotify}
                  apple={apple}
                  youtube={youtube}
                >
                  {longFormat && 'Escúchalo en'}
                </BroadcastRouter>
              ) : (
                <ToBeReleased>Disponible el {shortDate}</ToBeReleased>
              ))}
          </div>
          <div>
            {longFormat && spotify && (
              <ShareRouter
                shareUrl={`${PODCAST_URL}/${slug}`}
              >
                Comparte
              </ShareRouter>
            )}
          </div>
        </Fade>
        <ButtonSpace>
          {simplest && (
            <Link href={`/${slug}`}>
              <Button text={text || 'Seguir aprendiendo'} />
            </Link>
          )}
        </ButtonSpace>
      </div>
    </NewPod>
  );
};

export const EpisodePreview = React.memo(PreEpisodePreview);
